import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaCheck, FaTimes, FaStar, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import './Pricing.scss';

type PricingFeature = {
  name: string;
  included: boolean;
};

type PricingPackage = {
  name: string;
  price: number;
  features: PricingFeature[];
};

type PricingPackagesMap = {
  [key: string]: PricingPackage[];
};

const Pricing: React.FC = () => {

  // Set page title
  useEffect(() => {
    document.title = 'Pricing - Affordable Web Development Packages | App Developer Pro';
  }, []);

  // SEO constants
  const seoTitle = "Pricing - Web Development Packages | App Developer Pro";
  const seoDescription = "Transparent pricing for web development, mobile apps, UI/UX design, and digital marketing. Choose the best plan for your business needs.";
  const seoUrl = "https://appdeveloperpro.com/pricing";
  const seoImage = "https://appdeveloperpro.com/images/og-pricing.jpg";
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "OfferCatalog",
        "@id": "https://appdeveloperpro.com/pricing#offercatalog",
        "name": "Web Development Pricing Packages",
        "url": seoUrl,
        "description": seoDescription,
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Starter Web Development Package",
              "description": "Responsive website, basic SEO, contact form, and support."
            },
            "price": "2500",
            "priceCurrency": "USD"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Professional Web Development Package",
              "description": "Advanced SEO, e-commerce, CMS, and more."
            },
            "price": "5000",
            "priceCurrency": "USD"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Enterprise Web Development Package",
              "description": "Unlimited pages, mobile app, full e-commerce, analytics, and more."
            },
            "price": "10000",
            "priceCurrency": "USD"
          }
        ]
      },
      {
        "@type": "WebPage",
        "@id": "https://appdeveloperpro.com/pricing#webpage",
        "url": seoUrl,
        "name": seoTitle,
        "description": seoDescription,
        "inLanguage": "en-US",
        "isPartOf": {
          "@id": "https://appdeveloperpro.com/#website"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "url": seoImage
        },
        "datePublished": "2019-01-01T00:00:00+00:00",
        "dateModified": "2025-10-14T00:00:00+00:00"
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://appdeveloperpro.com"
          },
          {
            "@type": "ListItem", 
            "position": 2,
            "name": "Pricing",
            "item": seoUrl
          }
        ]
      }
    ]
  };
  const serviceFilters = [
    { key: 'web-application', label: 'Web Application' },
    { key: 'mobile-application', label: 'Mobile Application' },
    { key: 'ecommerce-development', label: 'E-commerce Solutions' },
    { key: 'ui-ux-design', label: 'UI/UX Design' },
    { key: 'seo-digital-marketing', label: 'SEO & Digital Marketing' }
  ];

  const pricingPackages: PricingPackagesMap = {
    'web-application': [
      {
        name: 'Starter',
        price: 800,
        features: [
          { name: 'Custom Web App', included: true },
          { name: 'Responsive Design', included: true },
          { name: 'Basic User Auth', included: true },
          { name: 'API Integration', included: true },
          { name: 'SEO Optimization', included: true },
          { name: '3 Month Support', included: true },
          { name: 'Payment Integration', included: false },
          { name: 'Admin Dashboard', included: false },
          { name: 'Multi-Tenant SaaS', included: false },
          { name: 'Priority Support', included: false }
        ]
      },
      {
        name: 'Professional',
        price: 1400,
        features: [
          { name: 'All Starter Features', included: true },
          { name: 'Advanced Auth & Roles', included: true },
          { name: 'Payment Integration', included: true },
          { name: 'Admin Dashboard', included: true },
          { name: '6 Months Support', included: true },
          { name: 'Multi-Tenant SaaS', included: false },
          { name: 'Custom Integrations', included: false },
          { name: '12 Months Maintenance', included: false }
        ]
      },
      {
        name: 'Enterprise',
        price: 2500,
        features: [
          { name: 'All Professional Features', included: true },
          { name: 'Multi-Tenant SaaS', included: true },
          { name: 'Custom Integrations', included: true },
          { name: 'Priority Support', included: true },
          { name: '12 Months Maintenance', included: true },
          { name: 'Dedicated Account Manager', included: true },
          { name: 'On-premise Deployment', included: false }
        ]
      }
    ],
    'mobile-application': [
      {
        name: 'Starter',
        price: 1000,
        features: [
          { name: 'iOS or Android App', included: true },
          { name: 'Basic UI', included: true },
          { name: 'Push Notifications', included: true },
          { name: 'SEO Optimization', included: true },
          { name: '3 Month Support', included: true },
          { name: 'App Store Publishing', included: false },
          { name: 'Analytics', included: false },
          { name: 'Custom Integrations', included: false },
          { name: 'Advanced Security', included: false }
        ]
      },
      {
        name: 'Professional',
        price: 1800,
        features: [
          { name: 'All Starter Features', included: true },
          { name: 'Cross-Platform', included: true },
          { name: 'App Store Publishing', included: true },
          { name: 'Analytics', included: true },
          { name: '6 Months Support', included: true },
          { name: 'Custom Integrations', included: false },
          { name: 'Advanced Security', included: false },
          { name: 'Priority Support', included: false },
          { name: '12 Months Maintenance', included: false }
        ]
      },
      {
        name: 'Enterprise',
        price: 3300,
        features: [
          { name: 'All Professional Features', included: true },
          { name: 'Custom Integrations', included: true },
          { name: 'Advanced Security', included: true },
          { name: 'Priority Support', included: true },
          { name: '12 Months Maintenance', included: true },
          { name: 'Dedicated Account Manager', included: true },
          { name: 'On-premise Deployment', included: false }
        ]
      }
    ],
    'ecommerce-development': [
      {
        name: 'Starter',
        price: 1000,
        features: [
          { name: 'Basic Storefront', included: true },
          { name: 'Payment Gateway', included: true },
          { name: 'Inventory Management', included: true },
          { name: 'Order Processing', included: true },
          { name: 'SEO Optimization', included: true },
          { name: '3 Month Support', included: true },
          { name: 'Multi-vendor', included: false },
          { name: 'Advanced Analytics', included: false },
          { name: 'Custom Integrations', included: false },
          { name: 'Priority Support', included: false }
        ]
      },
      {
        name: 'Professional',
        price: 1800,
        features: [
          { name: 'All Starter Features', included: true },
          { name: 'Multi-vendor', included: true },
          { name: 'Advanced Analytics', included: true },
          { name: '6 Months Support', included: true },
          { name: 'Custom Integrations', included: false },
          { name: 'Priority Support', included: false },
          { name: '12 Months Maintenance', included: false }
        ]
      },
      {
        name: 'Enterprise',
        price: 3300,
        features: [
          { name: 'All Professional Features', included: true },
          { name: 'Custom Integrations', included: true },
          { name: 'Priority Support', included: true },
          { name: '12 Months Maintenance', included: true },
          { name: 'Dedicated Account Manager', included: true },
          { name: 'On-premise Deployment', included: false }
        ]
      }
    ],
    
    'ui-ux-design': [
      {
        name: 'Starter',
        price: 300,
        features: [
          { name: 'Basic Wireframes', included: true },
          { name: '10 Page Designs', included: true },
          { name: 'Brand Colors & Fonts', included: true },
          { name: '1 Revision', included: true },
          { name: 'Design System', included: false },
          { name: 'Mobile & Tablet UI', included: false },
          { name: 'Custom Illustrations', included: false },
          { name: 'Unlimited Revisions', included: false }
        ]
      },
      {
        name: 'Professional',
        price: 500,
        features: [
          { name: 'All Starter Features', included: true },
          { name: 'Full Website Design', included: true },
          { name: 'Mobile & Tablet UI', included: true },
          { name: '3 Revisions', included: true },
          { name: 'Design System', included: true },
          { name: 'Custom Illustrations', included: false },
          { name: 'Unlimited Revisions', included: false },
          { name: 'Design Handoff', included: false }
        ]
      },
      {
        name: 'Enterprise',
        price: 1000,
        features: [
          { name: 'All Professional Features', included: true },
          { name: 'App UI/UX', included: true },
          { name: 'Custom Illustrations', included: true },
          { name: 'Unlimited Revisions', included: true },
          { name: 'Design Handoff', included: true },
          { name: 'Dedicated Designer', included: false },
          { name: 'Priority Support', included: false },
          { name: '12 Months Maintenance', included: false }
        ]
      }
    ],
    'seo-digital-marketing': [
      {
        name: 'Starter',
        price: 300,
        features: [
          { name: 'Basic SEO Audit', included: true },
          { name: 'On-page SEO', included: true },
          { name: '1 Keyword Group', included: true },
          { name: '1 Month Reporting', included: true },
          { name: 'Content Strategy', included: false },
          { name: 'Link Building', included: false },
          { name: 'PPC Management', included: false },
          { name: 'Conversion Optimization', included: false }
        ]
      },
      {
        name: 'Professional',
        price: 600,
        features: [
          { name: 'All Starter Features', included: true },
          { name: 'Content Strategy', included: true },
          { name: 'Link Building', included: true },
          { name: '3 Months Reporting', included: true },
          { name: 'PPC Management', included: false },
          { name: 'Conversion Optimization', included: false },
          { name: 'Priority Support', included: false },
          { name: '12 Months Maintenance', included: false }
        ]
      },
      {
        name: 'Enterprise',
        price: 1800,
        features: [
          { name: 'All Professional Features', included: true },
          { name: 'PPC Management', included: true },
          { name: 'Conversion Optimization', included: true },
          { name: '12 Months Reporting', included: true },
          { name: 'Dedicated Account Manager', included: true },
          { name: 'On-premise Deployment', included: false }
        ]
      }
    ]
  };

  const [selectedService, setSelectedService] = React.useState<string>('web-application');

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="web development pricing, app development pricing, website packages, digital marketing pricing, UI/UX design cost, business website cost, app developer pro" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="author" content="App Developer Pro" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#3b82f6" />
        <meta name="msapplication-TileColor" content="#3b82f6" />
        <meta name="rating" content="general" />
        <meta name="distribution" content="global" />
        <meta name="language" content="en" />
        <meta name="revisit-after" content="7 days" />
        <meta name="expires" content="never" />
        <meta name="coverage" content="worldwide" />
        <meta name="target" content="all" />
        <meta name="HandheldFriendly" content="true" />
        <meta name="MobileOptimized" content="320" />
        <link rel="canonical" href={seoUrl} />
        {/* Open Graph */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:site_name" content="App Developer Pro" />
        <meta property="og:image" content={seoImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Web Development Pricing Packages - App Developer Pro" />
        <meta property="og:locale" content="en_US" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@appdeveloperpro" />
        <meta name="twitter:creator" content="@appdeveloperpro" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:image:alt" content="Web Development Pricing Packages - App Developer Pro" />
        {/* Schema.org Markup */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <div className="pricing">
      <section className="pricing__hero">
        <motion.div
          className="pricing__hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>Affordable Web & Mobile Application Development Packages for Every Business</h1>
          <p>At App Developer Pro, we believe in clear, upfront pricing with no hidden fees for our web & mobile application development services. Our packages are designed to deliver maximum value, whether you’re a startup, small business, or established enterprise. Enjoy premium features, expert support, and scalable solutions tailored to your goals. Choose the perfect plan for your business. All plans include 
            responsive design, SEO optimization, and ongoing support.
          </p>
        </motion.div>
      </section>

      <section className="pricing__filter-section">
        <div className="pricing__filter-buttons">
          {serviceFilters.map((filter) => (
            <button
              key={filter.key}
              className={`pricing__filter-btn${selectedService === filter.key ? ' pricing__filter-btn--active' : ''}`}
              onClick={() => setSelectedService(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </section>

      <section className="pricing__packages-section">
        <div className="pricing__packages-grid">
          {(pricingPackages[selectedService] || []).map((pkg: PricingPackage) => (
            <div className={`pricing__package-card${pkg.name === 'Professional' ? ' pricing__package-card--popular' : ''}`} key={pkg.name}>
              {pkg.name === 'Professional' && (
                <span className="pricing__badge-popular">Most Popular</span>
              )}
              <div className="pricing__package-header">
                <h3>{pkg.name}</h3>
                <div className="pricing__package-price">${pkg.price.toLocaleString()}</div>
              </div>
              <ul className="pricing__package-features">
                {pkg.features.map((feature: PricingFeature, idx: number) => (
                  <li key={idx} className={feature.included ? 'feature-included' : 'feature-not-included'}>
                    {feature.included ? (
                      <span className="check-icon">&#10003;</span>
                    ) : (
                      <span className="cross-icon">&#10007;</span>
                    )}
                    <span>{feature.name}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="pricing__package-cta">Get Started <FaArrowRight /></Link>
            </div>
          ))}
        </div>
      </section>

      {/* Engaging SEO Section: Why Choose Us */}
      <section className="pricing__why-choose">
        <div className="pricing__why-choose-container">
          <h2>Why Choose App Developer Pro for Your Project?</h2>
          <div className="pricing__why-choose-grid">
            <div className="pricing__why-choose-item">
              <span className="pricing__why-choose-icon" role="img" aria-label="Expert Team">👨‍💻</span>
              <h4>Expert Team</h4>
              <p>Our certified developers and designers deliver innovative, scalable solutions for every business size.</p>
            </div>
            <div className="pricing__why-choose-item">
              <span className="pricing__why-choose-icon" role="img" aria-label="Transparent Pricing">💡</span>
              <h4>Transparent Pricing</h4>
              <p>No hidden fees. You get clear, upfront pricing and flexible packages tailored to your needs.</p>
            </div>
            <div className="pricing__why-choose-item">
              <span className="pricing__why-choose-icon" role="img" aria-label="Proven Results">🚀</span>
              <h4>Proven Results</h4>
              <p>We’ve helped 200+ clients launch successful digital products and grow their businesses online.</p>
            </div>
            <div className="pricing__why-choose-item">
              <span className="pricing__why-choose-icon" role="img" aria-label="Ongoing Support">🤝</span>
              <h4>Ongoing Support</h4>
              <p>Enjoy dedicated support, regular updates, and expert guidance throughout your project journey.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Engaging SEO Section: How Our Pricing Works */}
      <section className="pricing__how-it-works">
        <div className="pricing__how-it-works-container">
          <h2>How Our Pricing Works</h2>
          <p>Our pricing is designed to be simple, transparent, and flexible. Choose a package or request a custom quote. We’ll help you find the best fit for your goals and budget.</p>
          <ul className="pricing__how-it-works-list">
            <li><strong>Free Consultation:</strong> Get expert advice and a detailed proposal for your project.</li>
            <li><strong>Flexible Packages:</strong> Select from our pre-built packages or customize your own.</li>
            <li><strong>Milestone Payments:</strong> Pay as you go, with clear deliverables at every stage.</li>
            <li><strong>No Hidden Fees:</strong> You only pay for what you need—no surprises, ever.</li>
          </ul>
        </div>
      </section>

      <section className="pricing__faq">
        <motion.div
          className="pricing__faq-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2>Frequently Asked Questions</h2>
          <div className="pricing__faq-grid">
            <div className="pricing__faq-col">
              <div className="pricing__faq-item">
                <h3>What's included in the support period?</h3>
                <p>Our support includes bug fixes, minor updates, security patches, and technical assistance via email and phone.</p>
              </div>
              <div className="pricing__faq-item">
                <h3>Can I upgrade my plan later?</h3>
                <p>Yes! You can upgrade your plan at any time. We'll credit the value of your current plan toward the upgrade.</p>
              </div>
              <div className="pricing__faq-item">
                <h3>Do you offer custom solutions?</h3>
                <p>Absolutely! If none of our standard plans fit your needs, we'd be happy to create a custom solution for you.</p>
              </div>
            </div>
            <div className="pricing__faq-col">
              <div className="pricing__faq-item">
                <h3>How long does a typical project take?</h3>
                <p>Most projects are completed in 2-6 weeks, depending on complexity and requirements.</p>
              </div>
              <div className="pricing__faq-item">
                <h3>Will my website/app be mobile-friendly?</h3>
                <p>Yes, all our solutions are fully responsive and optimized for all devices.</p>
              </div>
              <div className="pricing__faq-item">
                <h3>What payment methods do you accept?</h3>
                <p>We accept bank transfer, credit/debit cards, and PayPal for your convenience.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
    </>
  );
};

export default Pricing;
